/**
 * Kiosk-specific types for device customization
 * Extends generic DeviceUICustomization with kiosk-specific icons and configurations
 */

import { AssetReference } from '../../core/types/asset-types';
import { DeviceUICustomization } from '../../core/types/device-config-types';

/**
 * Connector types supported by EV kiosks
 */
export type ConnectorType = 'chademo' | 'ccs' | 'type2' | 'gbt';

/**
 * Connector status types
 */
export type ConnectorStatus = 'available' | 'connected' | 'occupied' | 'unavailable';

/**
 * Kiosk-specific icons
 * All icons used in kiosk UI
 */
export interface KioskIcons {
  // Branding
  logo: AssetReference;

  // Media 4 and 5 logos (new)
  media4Logo?: AssetReference;
  media5Logo?: AssetReference;

  // Idle screen media (images or Lottie animations)
  media4IdleScreen?: AssetReference;
  media5IdleScreen?: AssetReference;

  // Connector type icons
  chademo: AssetReference;
  ccs: AssetReference;
  type2: AssetReference;
  gbt?: AssetReference; // Optional GB/T connector

  // Payment method icons
  visa: AssetReference;
  mastercard: AssetReference;
  applePay: AssetReference;
  googlePay: AssetReference;

  // UI icons
  menuIcon: AssetReference;
  languageIcon: AssetReference;
  arrowIcon?: AssetReference; // Optional arrow for card navigation
}

/**
 * Kiosk UI customization configuration
 * Specialized version of DeviceUICustomization for kiosk devices
 */
export type KioskUICustomization = DeviceUICustomization<KioskIcons>;

/**
 * Connector information displayed on kiosk
 */
export interface ConnectorInfo {
  /** Connector ID (e.g., "1", "2", "A") */
  id: string;

  /** Connector type (CHAdeMO, CCS, Type 2, GB/T) */
  type: ConnectorType;

  /** Power output in kW */
  power: number;

  /** Current status */
  status: ConnectorStatus;

  /** Optional EVSE ID */
  evseId?: string;

  /** Optional location name */
  locationName?: string;
}

/**
 * Kiosk screen configuration
 * Determines which screen is being customized
 */
export type KioskScreenType = 'select-charger' | 'idle' | 'charging' | 'payment';

/**
 * Kiosk form factor details
 */
export interface KioskFormFactor {
  /** Form factor identifier */
  id: '4inch' | '5inch';

  /** Screen dimensions */
  dimensions: {
    width: number;
    height: number;
  };

  /** Card dimensions for this form factor */
  cardSize: {
    width: number;
    height: number;
  };

  /** Maximum number of cards visible at once */
  maxVisibleCards: number;

  /** Display name */
  displayName: string;
}

/**
 * Required icons for kiosk validation
 * Used by validation rules to ensure all necessary icons are present
 */
export const REQUIRED_KIOSK_ICONS: Array<keyof KioskIcons> = [
  'logo',
  'chademo',
  'ccs',
  'type2',
  'visa',
  'mastercard',
  'applePay',
  'googlePay',
  'menuIcon',
  'languageIcon',
];

/**
 * Sample connector data for preview
 */
export const SAMPLE_CONNECTORS: ConnectorInfo[] = [
  {
    id: '1',
    type: 'chademo',
    power: 50,
    status: 'available',
    evseId: 'EVSE-001',
  },
  {
    id: '2',
    type: 'ccs',
    power: 150,
    status: 'connected',
    evseId: 'EVSE-002',
  },
  {
    id: '3',
    type: 'type2',
    power: 22,
    status: 'occupied',
    evseId: 'EVSE-003',
  },
  {
    id: '4',
    type: 'ccs',
    power: 350,
    status: 'unavailable',
    evseId: 'EVSE-004',
  },
  {
    id: '5',
    type: 'chademo',
    power: 100,
    status: 'available',
    evseId: 'EVSE-005',
  },
];

/**
 * Crop area coordinates for image cropping
 */
export interface CropArea {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Pending logo upload state (before Save)
 * Holds the file and preview information
 */
export interface PendingLogoUpload {
  file: File;
  originalImageUrl: string; // URL of the original uncropped image
  previewUrl: string; // URL of the cropped image (or original if not cropped yet)
  cropArea?: CropArea;
  croppedImageBlob?: Blob;
}

/**
 * Media type for uploads
 */
export type MediaType = 'image' | 'lottie';

/**
 * Pending media upload state (for idle screens supporting images and Lottie)
 * Extends PendingLogoUpload with media type detection
 */
export interface PendingMediaUpload {
  file: File;
  mediaType: MediaType; // 'image' or 'lottie'
  originalImageUrl: string; // URL of the original uncropped image (for images only)
  previewUrl: string; // URL of the cropped image or Lottie JSON
  cropArea?: CropArea; // Only applicable for images
  croppedImageBlob?: Blob; // Only applicable for images
}

/**
 * Request for uploading an icon/logo asset
 * Used by UploadKioskIconUseCase
 */
export interface UploadIconRequest {
  file: File;
  iconName: string;
  iconType: 'logo' | 'icon';
  operatorId: string;
  description?: string;
}
